// src/pages/OeeRep1.js
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { apiClient } from "../api";
import DoughnutChart from "./chart8";

import {
  getMachinesByCompany,
  getOpsCountRep1,
  getLotsCountRep1,
  getInactiveHoursRep1,
  getReportedHoursRep1,
  getProductsSummaryRep1,
  getOpsPerHourRep1,
} from "../api";

const OeeRep1 = ({ id_c: idCProp }) => {
  const { t } = useContext(LanguageContext);

  // Company e IDs desde localStorage
  const company = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("company")) || {};
    } catch {
      return {};
    }
  }, []);

  const id_c =
    idCProp ??
    (() => {
      try {
        return JSON.parse(localStorage.getItem("company"))?.id_c ?? null;
      } catch {
        return null;
      }
    })();

  // (no lo usas actualmente, pero lo dejo por si luego lo necesitas)
  const id_u = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"))?.id_u ?? null;
    } catch {
      return null;
    }
  }, []);

  // ---------------------------
  // Estado UI: rango de fechas + máquinas
  // ---------------------------
  const [dateFrom, setDateFrom] = useState(() => {
    return localStorage.getItem("oee_rep1_date_from") || ""; // YYYY-MM-DD
  });

  const [dateTo, setDateTo] = useState(() => {
    return localStorage.getItem("oee_rep1_date_to") || ""; // YYYY-MM-DD
  });

  // Guardamos máquinas por compañía, para no mezclar selecciones entre empresas
  const storeKeyMachines = `oee_rep1_machine_codes_${id_c || "na"}`;

  const [allMachines, setAllMachines] = useState([]); // [{cod_maq, name_m, ...}]
  const [machineCodes, setMachineCodes] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(storeKeyMachines) || "[]");
    } catch {
      return [];
    }
  });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState(""); // filtro de códigos en dropdown

  // ---------------------------
  // Estados para KPIs
  // ---------------------------
  const [operations, setOperations] = useState(0);
  const [loadingOps, setLoadingOps] = useState(false);
  const [errorOps, setErrorOps] = useState(null);

  const [lots, setLots] = useState(0);
  const [loadingLots, setLoadingLots] = useState(false);
  const [errorLots, setErrorLots] = useState(null);

  const [inactiveHours, setInactiveHours] = useState(0);
  const [loadingInactive, setLoadingInactive] = useState(false);
  const [errorInactive, setErrorInactive] = useState(null);

  const [reportedHours, setReportedHours] = useState(0);
  const [loadingReported, setLoadingReported] = useState(false);
  const [errorReported, setErrorReported] = useState(null);

  const [conformingProducts, setConformingProducts] = useState(0);
  const [nonConformingProducts, setNonConformingProducts] = useState(0);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [errorProducts, setErrorProducts] = useState(null);

  const [standardOpsPerHour, setStandardOpsPerHour] = useState(0);
  const [realOpsPerHour, setRealOpsPerHour] = useState(0);
  const [loadingOpsPerHour, setLoadingOpsPerHour] = useState(false);
  const [errorOpsPerHour, setErrorOpsPerHour] = useState(null);

  // ---------------------------
  // OEE cálculos
  // ---------------------------
  const efficiencyPct = useMemo(() => {
    if (!standardOpsPerHour || standardOpsPerHour === 0) return 0;
    const ratio = realOpsPerHour / standardOpsPerHour;
    return Number((ratio * 100).toFixed(2));
  }, [realOpsPerHour, standardOpsPerHour]);

  const conformityPct = useMemo(() => {
    const total = conformingProducts + nonConformingProducts;
    if (!total || total === 0) return 0;
    const ratio = conformingProducts / total;
    return Number((ratio * 100).toFixed(2));
  }, [conformingProducts, nonConformingProducts]);

  const availabilityPct = useMemo(() => {
    const total = reportedHours + inactiveHours;
    if (!total || total === 0) return 0;
    const ratio = reportedHours / total;
    return Number((ratio * 100).toFixed(2));
  }, [reportedHours, inactiveHours]);

  const oeePct = useMemo(() => {
    if (!efficiencyPct || !conformityPct || !availabilityPct) return 0;

    const eff = efficiencyPct / 100;
    const con = conformityPct / 100;
    const disp = availabilityPct / 100;

    const result = eff * con * disp;
    return Number((result * 100).toFixed(2));
  }, [efficiencyPct, conformityPct, availabilityPct]);

  const oee = oeePct;
  const efficiency = efficiencyPct;
  const conformity = conformityPct;
  const availability = availabilityPct;

  // ---------------------------
  // (Opcional) cargar machines desde getMachinesByCompany (no es estrictamente necesario)
  // lo dejo por compatibilidad con tu estructura
  // ---------------------------
  const [machines, setMachines] = useState([]);
  useEffect(() => {
    const loadMachinesSimple = async () => {
      try {
        if (!id_c) return;
        const res = await getMachinesByCompany(id_c);
        setMachines(res.machines || []);
      } catch (err) {
        console.error("❌ Error cargando máquinas (simple):", err);
      }
    };
    loadMachinesSimple();
  }, [id_c]);

  // ---------------------------
  // Helpers UI: filtro y etiquetas de selector
  // ---------------------------
  const filteredMachines = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return allMachines;
    return allMachines.filter((m) =>
      (m.cod_maq || "").toLowerCase().includes(s)
    );
  }, [q, allMachines]);

  const allSelected =
    machineCodes.length && machineCodes.length === allMachines.length;
  const someSelected =
    machineCodes.length > 0 && machineCodes.length < allMachines.length;

  const labelPicker = useMemo(() => {
    if (!allMachines.length) return t("Oee16");
    if (!machineCodes.length) return t("Oee17");
    if (allSelected) return t("Oee18");
    return `${machineCodes.length} `;
  }, [allMachines.length, machineCodes.length, allSelected, t]);

  // Cierra dropdown si clic fuera
  const dropdownRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setPickerOpen(false);
    };
    if (pickerOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [pickerOpen]);

  // ---------------------------
  // Cargar máquinas de la empresa (para el selector)
  // ---------------------------
  useEffect(() => {
    const loadMachines = async () => {
      if (!id_c) return;
      try {
        const { data } = await apiClient.get(`/company/${id_c}/machines`);
        const list =
          data?.machines?.map((m) => ({
            cod_maq: m.cod_maq,
            name_m: m.name_m,
            status_m: m.status_m,
            last_activity: m.last_activity,
          })) || [];

        setAllMachines(list);

        // Si no hay selección guardada, seleccionar todas por defecto
        if (!machineCodes.length && list.length) {
          const allCodes = list.map((m) => m.cod_maq);
          setMachineCodes(allCodes);
          localStorage.setItem(storeKeyMachines, JSON.stringify(allCodes));
        }
      } catch (err) {
        console.error("Error loading machines:", err);
      }
    };

    loadMachines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_c]);

  // ---------------------------
  // Acciones selector máquinas
  // ---------------------------
  const toggleAll = () => {
    if (allSelected) {
      setMachineCodes([]);
      localStorage.setItem(storeKeyMachines, JSON.stringify([]));
    } else {
      const allCodes = allMachines.map((m) => m.cod_maq);
      setMachineCodes(allCodes);
      localStorage.setItem(storeKeyMachines, JSON.stringify(allCodes));
    }
  };

  const toggleOne = (code) => {
    const set = new Set(machineCodes);
    if (set.has(code)) set.delete(code);
    else set.add(code);
    const next = Array.from(set);
    setMachineCodes(next);
    localStorage.setItem(storeKeyMachines, JSON.stringify(next));
  };

  // ---------------------------
  // (Recomendado) Si no hay fechas, setea un default: mes actual hasta hoy
  // ---------------------------
  useEffect(() => {
    if (dateFrom && dateTo) return;

    const pad = (n) => String(n).padStart(2, "0");
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    const d = today.getDate();

    const firstDay = `${y}-${pad(m)}-01`;
    const todayStr = `${y}-${pad(m)}-${pad(d)}`;

    if (!dateFrom) {
      setDateFrom(firstDay);
      localStorage.setItem("oee_rep1_date_from", firstDay);
    }
    if (!dateTo) {
      setDateTo(todayStr);
      localStorage.setItem("oee_rep1_date_to", todayStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------
  // APPLY (consulta con rango inclusivo)
  // ---------------------------
  const onApply = async () => {
    if (!id_c) {
      console.warn("⚠️ No hay id_c disponible para calcular OeeRep1");
      return;
    }

    if (!dateFrom || !dateTo) {
      console.warn("⚠️ Debes seleccionar fecha inicio y fecha fin");
      return;
    }

    if (dateFrom > dateTo) {
      console.warn("⚠️ La fecha inicio no puede ser mayor que la fecha fin");
      return;
    }

    const effectiveMachineCodes =
      machineCodes.length > 0 ? machineCodes : allMachines.map((m) => m.cod_maq);

    // Reset loading/error
    setLoadingOps(true);
    setLoadingLots(true);
    setLoadingInactive(true);
    setLoadingReported(true);
    setLoadingProducts(true);
    setLoadingOpsPerHour(true);

    setErrorOps(null);
    setErrorLots(null);
    setErrorInactive(null);
    setErrorReported(null);
    setErrorProducts(null);
    setErrorOpsPerHour(null);

    try {
      const [opsR, lotsR, inactR, repR, prodR, opsHourR] =
        await Promise.allSettled([
          getOpsCountRep1({
            id_c,
            dateFrom,
            dateTo,
            machineCodes: effectiveMachineCodes,
          }),
          getLotsCountRep1({
            id_c,
            dateFrom,
            dateTo,
            machineCodes: effectiveMachineCodes,
          }),
          getInactiveHoursRep1({
            id_c,
            dateFrom,
            dateTo,
            machineCodes: effectiveMachineCodes,
          }),
          getReportedHoursRep1({
            id_c,
            dateFrom,
            dateTo,
            machineCodes: effectiveMachineCodes,
          }),
          getProductsSummaryRep1({
            id_c,
            dateFrom,
            dateTo,
            machineCodes: effectiveMachineCodes,
          }),
          getOpsPerHourRep1({
            id_c,
            dateFrom,
            dateTo,
            machineCodes: effectiveMachineCodes,
          }),
        ]);

      // OPERACIONES
      if (opsR.status === "fulfilled") {
        setOperations(Number(opsR.value?.total_act ?? 0));
      } else {
        setErrorOps("Error al calcular operaciones");
      }

      // LOTES
      if (lotsR.status === "fulfilled") {
        setLots(Number(lotsR.value?.total_lots || 0));
      } else {
        setErrorLots("Error al calcular lotes");
      }

      // HORAS INACTIVAS
      if (inactR.status === "fulfilled") {
        setInactiveHours(Number(inactR.value?.total_inactive_hours || 0));
      } else {
        setErrorInactive("Error al calcular horas inactivas");
      }

      // HORAS REPORTADAS
      if (repR.status === "fulfilled") {
        setReportedHours(Number(repR.value?.total_reported_hours || 0));
      } else {
        setErrorReported("Error al calcular horas reportadas");
      }

      // PRODUCTOS OK / NC
      if (prodR.status === "fulfilled") {
        setConformingProducts(Number(prodR.value?.total_ok || 0));
        setNonConformingProducts(Number(prodR.value?.total_nc || 0));
      } else {
        setErrorProducts("Error al calcular productos conformes/no conformes");
      }

      // OPS STD/H y OPS REALES/H
      if (opsHourR.status === "fulfilled") {
        setStandardOpsPerHour(
          Number(opsHourR.value?.standard_ops_per_hour || 0)
        );
        setRealOpsPerHour(Number(opsHourR.value?.real_ops_per_hour || 0));
      } else {
        setErrorOpsPerHour("Error al calcular operaciones por hora");
      }
    } catch (err) {
      console.error("❌ Error general aplicando cálculos OeeRep1:", err);
    } finally {
      setLoadingOps(false);
      setLoadingLots(false);
      setLoadingInactive(false);
      setLoadingReported(false);
      setLoadingProducts(false);
      setLoadingOpsPerHour(false);
    }
  };

  return (
    <div className="box1">
      <div className="box13">
        <div>
          <table className="table3" style={{ width: "100%" }}>
            <tbody>
              <tr>
                <td style={{ width: "20%" }}>
                  <img
                    className="logo6"
                    src={company?.logo_c}
                    alt="Company Logo"
                  />
                </td>

                {/* FECHA INICIO */}
                <td className="text3" style={{ width: "10%" }}>
                  Fecha inicio
                </td>
                <td style={{ width: "20%" }}>
                  <input
                    type="date"
                    style={{ textAlign: "center", width: "100%" }}
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      localStorage.setItem("oee_rep1_date_from", e.target.value);
                    }}
                  />
                </td>

                {/* FECHA FIN */}
                <td className="text3" style={{ width: "10%" }}>
                  Fecha fin
                </td>
                <td style={{ width: "20%" }}>
                  <input
                    type="date"
                    style={{ textAlign: "center", width: "100%" }}
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      localStorage.setItem("oee_rep1_date_to", e.target.value);
                    }}
                  />
                </td>
              </tr>

              <tr style={{ width: "100%", height: "80px" }}>
                <td className="text3" style={{ width: "20%" }}>
                  {t("Oee14")}
                </td>

                <td colSpan={3} style={{ paddingTop: 8 }}>
                  {/* Machine Picker */}
                  <div ref={dropdownRef} style={{ position: "relative" }}>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setPickerOpen((o) => !o)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #ddd",
                        background: "#fff",
                      }}
                    >
                      {labelPicker}
                    </button>

                    {pickerOpen && (
                      <div
                        style={{
                          position: "absolute",
                          zIndex: 30,
                          top: "calc(100% + 6px)",
                          left: 0,
                          width: "100%",
                          background: "#fff",
                          border: "1px solid #ddd",
                          borderRadius: 8,
                          padding: 8,
                          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                        }}
                      >
                        <input
                          type="text"
                          placeholder={t("Oee20")}
                          value={q}
                          onChange={(e) => setQ(e.target.value)}
                          style={{
                            width: "100%",
                            marginBottom: 8,
                            padding: 6,
                            borderRadius: 6,
                            border: "1px solid #ddd",
                          }}
                        />

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: 6,
                          }}
                        >
                          <input
                            id="chk-all"
                            type="checkbox"
                            checked={!!allSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = !!someSelected;
                            }}
                            onChange={toggleAll}
                            style={{ marginRight: 6 }}
                          />
                          <label htmlFor="chk-all">{t("Oee21")}</label>
                        </div>

                        <div
                          style={{
                            maxHeight: 260,
                            overflowY: "auto",
                            paddingRight: 4,
                          }}
                        >
                          {filteredMachines.map((m) => {
                            const checked = machineCodes.includes(m.cod_maq);
                            return (
                              <label
                                key={m.cod_maq}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  padding: "4px 0",
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleOne(m.cod_maq)}
                                />
                                <span style={{ fontSize: 14 }}>
                                  {m.cod_maq || "(sin código)"}
                                </span>
                              </label>
                            );
                          })}

                          {filteredMachines.length === 0 && (
                            <div
                              style={{
                                color: "#666",
                                fontSize: 13,
                                padding: "6px 0",
                              }}
                            >
                              Sin resultados
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </td>

                <td style={{ width: "20%" }}>
                  <button
                    onClick={onApply}
                    className="button5"
                    style={{ textAlign: "center", width: "100%" }}
                  >
                    {t("Oee15")}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="dashboard-grid">
          {/* LOTES */}
          <div className="card">
            <h1>{loadingLots ? "…" : lots}</h1>
            <p>{t("Oee2")}</p>
            {errorLots && <small style={{ color: "#c00" }}>{errorLots}</small>}
          </div>

          {/* OPERACIONES */}
          <div className="card">
            <h1>{loadingOps ? "…" : operations}</h1>
            <p>{t("Oee12")}</p>
            {errorOps && <small style={{ color: "#c00" }}>{errorOps}</small>}
          </div>

          {/* HORAS REPORTADAS */}
          <div className="card">
            <h1>{loadingReported ? "…" : reportedHours}</h1>
            <p>{t("Oee3")}</p>
            {errorReported && (
              <small style={{ color: "#c00" }}>{errorReported}</small>
            )}
          </div>

          {/* HORAS NO ACTIVAS */}
          <div className="card">
            <h1>{loadingInactive ? "…" : inactiveHours}</h1>
            <p>{t("Oee4")}</p>
            {errorInactive && (
              <small style={{ color: "#c00" }}>{errorInactive}</small>
            )}
          </div>

          {/* PRODUCTOS OK */}
          <div className="card">
            <h1>{loadingProducts ? "…" : conformingProducts}</h1>
            <p>{t("Oee5")}</p>
            {errorProducts && (
              <small style={{ color: "#c00" }}>{errorProducts}</small>
            )}
          </div>

          {/* PRODUCTOS NC */}
          <div className="card">
            <h1>{loadingProducts ? "…" : nonConformingProducts}</h1>
            <p>{t("Oee6")}</p>
            {errorProducts && (
              <small style={{ color: "#c00" }}>{errorProducts}</small>
            )}
          </div>

          {/* OPS STD/H */}
          <div className="card">
            <h1>{loadingOpsPerHour ? "…" : standardOpsPerHour}</h1>
            <p>{t("Oee7")}</p>
            {errorOpsPerHour && (
              <small style={{ color: "#c00" }}>{errorOpsPerHour}</small>
            )}
          </div>

          {/* OPS REALES/H */}
          <div className="card">
            <h1>{loadingOpsPerHour ? "…" : realOpsPerHour}</h1>
            <p>{t("Oee8")}</p>
            {errorOpsPerHour && (
              <small style={{ color: "#c00" }}>{errorOpsPerHour}</small>
            )}
          </div>

          {/* Gauge central OEE */}
          <div className="panel hero gauge-main">
            <DoughnutChart value={oee} label={t("Oee1")} size="large" />
          </div>

          {/* Tres gauges */}
          <div className="panel right-col r1 gauge-small">
            <DoughnutChart value={efficiency} label={t("Oee9")} size="small" />
          </div>

          <div className="panel right-col r2 gauge-small">
            <DoughnutChart value={conformity} label={t("Oee10")} size="small" />
          </div>

          <div className="panel right-col r3 gauge-small">
            <DoughnutChart value={availability} label={t("Oee11")} size="small" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OeeRep1;
